import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import logoNavbar from '../assets/logo-navbar.png';      // 133×48 transparent
import logoNavbarLg from '../assets/logo-navbar-lg.png'; // 167×60 transparent

const SERVICES = [
  { label: 'Complete Modular Works',       path: '/services/complete-modular-site-work', icon: '🗄️' },
  { label: 'Interior Design',              path: '/services/interior-design',             icon: '🛋️' },
  { label: 'Home Lighting',               path: '/services/home-lighting',               icon: '💡' },
  { label: 'Modular Kitchen',             path: '/services/modular-kitchen',             icon: '🍳' },
  { label: 'False Ceiling & Painting',    path: '/services/false-ceiling-painting',      icon: '🎨' },
  { label: 'Aluminium & Glass Works',     path: '/services/aluminium-glass-works',       icon: '🪟' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [svcOpen,  setSvcOpen]  = useState(false);
  // Closing on a short delay forgives the diagonal cursor path people take
  // toward a menu item, which would otherwise clip a corner and shut it.
  const svcTimer = useRef(null);
  const openServices = () => { clearTimeout(svcTimer.current); setSvcOpen(true); };
  const closeServicesSoon = () => {
    clearTimeout(svcTimer.current);
    svcTimer.current = setTimeout(() => setSvcOpen(false), 220);
  };
  useEffect(() => () => clearTimeout(svcTimer.current), []);
  const [mSvcOpen, setMSvcOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 70);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => { setMenuOpen(false); setSvcOpen(false); setMSvcOpen(false); }, [location]);

  const lnk = (active) => ({
    fontSize: '0.68rem', letterSpacing: '0.16em', textTransform: 'uppercase',
    fontWeight: '500', color: active ? '#F5A800' : '#ffffff',
    transition: 'color 0.3s', padding: '0.25rem 0',
    borderBottom: active ? '1px solid #F5A800' : '1px solid transparent',
    whiteSpace: 'nowrap',
  });

  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
      transition: 'all 0.5s cubic-bezier(0.25,0.46,0.45,0.94)',
      background: scrolled || menuOpen ? 'rgba(0,0,0,0.97)' : 'transparent',
      backdropFilter: scrolled ? 'blur(24px)' : 'none',
      borderBottom: scrolled ? '1px solid rgba(245,168,0,0.15)' : '1px solid transparent',
      padding: scrolled ? '0.55rem 0' : '1rem 0',
    }}>
      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.5rem' }}>

        {/* ═══ LOGO — transparent Wedoo image, no text at all ═══ */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <img
            src={scrolled ? logoNavbar : logoNavbarLg}
            alt="Wedoo Interiors — Interiors"
            /* Eager: it is the first thing painted. Intrinsic size reserves the
               space so the header does not jump as it loads. */
            fetchpriority="high"
            width={scrolled ? 133 : 167}
            height={scrolled ? 42 : 52}
            style={{
              height: scrolled ? '42px' : '52px',
              width: 'auto',
              objectFit: 'contain',
              transition: 'all 0.4s ease',
              display: 'block',
            }}
          />
        </Link>

        {/* ═══ Desktop nav ═══ */}
        <div className="d-nav" style={{ display: 'flex', alignItems: 'center', gap: '2rem', flexShrink: 0 }}>
          <NavLink to="/" end style={({ isActive }) => lnk(isActive)}>Home</NavLink>
          <NavLink to="/about"     style={({ isActive }) => lnk(isActive)}>About</NavLink>

          {/* Services dropdown */}
          <div style={{ position: 'relative' }}
            onMouseEnter={openServices}
            onMouseLeave={closeServicesSoon}>
            <span style={{
              ...lnk(location.pathname.startsWith('/services')),
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem',
            }}>
              Services
              <svg width="9" height="5" viewBox="0 0 10 6" fill="none"
                style={{ transition: 'transform 0.3s', transform: svcOpen ? 'rotate(180deg)' : 'none', flexShrink: 0 }}>
                <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </span>
            {svcOpen && (
              // Wrapper spans the gap: transparent padding, but hoverable, so
              // moving down to the menu no longer passes over dead space.
              <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                            paddingTop: '1rem', zIndex: 300 }}>
              <div style={{
                background: 'rgba(8,8,8,0.99)', backdropFilter: 'blur(24px)',
                border: '1px solid rgba(245,168,0,0.2)', borderRadius:'var(--radius)', minWidth: '285px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
              }}>
                {SERVICES.map(s => (
                  <Link key={s.path} to={s.path} style={{
                    display: 'flex', alignItems: 'center', gap: '0.85rem',
                    padding: '0.7rem 1.4rem', fontSize: '0.77rem', color: '#ddd',
                    transition: 'all 0.22s', letterSpacing: '0.04em',
                    borderLeft: '2px solid transparent',
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                  }}
                    onMouseEnter={e => {
                      e.currentTarget.style.color = '#F5A800';
                      e.currentTarget.style.borderLeftColor = '#F5A800';
                      e.currentTarget.style.paddingLeft = '1.7rem';
                      e.currentTarget.style.background = 'rgba(245,168,0,0.05)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.color = '#ddd';
                      e.currentTarget.style.borderLeftColor = 'transparent';
                      e.currentTarget.style.paddingLeft = '1.4rem';
                      e.currentTarget.style.background = 'transparent';
                    }}>
                    <span style={{ fontSize: '0.95rem', width: '18px', flexShrink: 0 }}>{s.icon}</span>
                    {s.label}
                  </Link>
                ))}
                <div style={{ padding: '0.65rem 1.4rem', borderTop: '1px solid rgba(245,168,0,0.12)' }}>
                  <Link to="/services" style={{ fontSize: '0.62rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#F5A800' }}>
                    View All Services →
                  </Link>
                </div>
              </div>
              </div>
            )}
          </div>

          <NavLink to="/portfolio" style={({ isActive }) => lnk(isActive)}>Portfolio</NavLink>
          <NavLink to="/gallery"   style={({ isActive }) => lnk(isActive)}>Gallery</NavLink>
          <NavLink to="/offers"    style={({ isActive }) => lnk(isActive)}>Offers</NavLink>
          <NavLink to="/careers"   style={({ isActive }) => lnk(isActive)}>Careers</NavLink>
          <NavLink to="/contact"   style={({ isActive }) => lnk(isActive)}>Contact</NavLink>

          <Link to="/get-free-quote" className="btn btn-gold"
            style={{ padding: '0.6rem 1.4rem', fontSize: '0.63rem', letterSpacing: '0.14em' }}>
            Free Consultation
          </Link>
        </div>

        {/* ═══ Hamburger ═══ */}
        <button onClick={() => setMenuOpen(p => !p)} className="hamburger" aria-label="Toggle menu"
          style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', flexDirection: 'column', gap: '5px', alignItems: 'center' }}>
          {[0,1,2].map(i => (
            <span key={i} style={{
              display: 'block', width: '24px', height: '1.5px', background: '#fff',
              transition: 'all 0.3s ease',
              transform: menuOpen
                ? i===0 ? 'rotate(45deg) translate(4.5px,4.5px)'
                : i===2 ? 'rotate(-45deg) translate(4.5px,-4.5px)'
                : 'scaleX(0)'
                : 'none',
              opacity: menuOpen && i===1 ? 0 : 1,
            }} />
          ))}
        </button>
      </div>

      {/* ═══ Mobile Menu ═══ */}
      {menuOpen && (
        <div style={{
          background: 'rgba(0,0,0,0.99)', backdropFilter: 'blur(24px)',
          borderTop: '1px solid rgba(245,168,0,0.12)', padding: '1.25rem 1.5rem',
          animation: 'mSlideDown 0.28s ease both',
        }}>
          {/* Logo in mobile menu */}
          <div style={{ paddingBottom: '1rem', marginBottom: '0.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          </div>

          {[
            { l: 'Home',      p: '/' },
            { l: 'About',     p: '/about' },
            { l: 'Portfolio', p: '/portfolio' },
            { l: 'Gallery',   p: '/gallery' },
            { l: 'Offers',    p: '/offers' },
            { l: 'Careers',   p: '/careers' },
            { l: 'Contact',   p: '/contact' },
          ].map(i => (
            <Link key={i.p} to={i.p} style={{
              display: 'block', padding: '0.85rem 0',
              borderBottom: '1px solid rgba(255,255,255,0.05)',
              fontSize: '0.82rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#fff',
            }}>{i.l}</Link>
          ))}

          {/* Mobile Services accordion */}
          <div>
            <button onClick={() => setMSvcOpen(p => !p)} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              width: '100%', padding: '0.85rem 0', cursor: 'pointer', background: 'none',
              borderBottom: '1px solid rgba(255,255,255,0.05)',
              fontSize: '0.82rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#fff',
            }}>
              Services
              <span style={{ color: '#F5A800', fontSize: '0.65rem', transition: '0.3s', transform: mSvcOpen ? 'rotate(180deg)' : 'none' }}>▾</span>
            </button>
            {mSvcOpen && (
              <div style={{ paddingLeft: '0.75rem', paddingBottom: '0.25rem' }}>
                {SERVICES.map(s => (
                  <Link key={s.path} to={s.path} style={{
                    display: 'flex', alignItems: 'center', gap: '0.6rem',
                    padding: '0.6rem 0', fontSize: '0.78rem', color: '#F5A800',
                    borderBottom: '1px solid rgba(255,255,255,0.03)',
                  }}>
                    <span>{s.icon}</span> {s.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Mobile CTAs */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1.25rem' }}>
            <Link to="/get-free-quote" className="btn btn-gold" style={{ justifyContent: 'center', fontSize: '0.75rem' }}>
              Get Free Consultation
            </Link>
            <a href="tel:+919686583195" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              color: '#F5A800', fontSize: '0.88rem', letterSpacing: '0.08em', paddingTop: '0.25rem',
            }}>📞 +919686583195</a>
            <a href="https://api.whatsapp.com/send?phone=919686583195&text=Hi%20Wedoo%20Interiors!"
              target="_blank" rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', color: '#25D366', fontSize: '0.82rem' }}>
              💬 WhatsApp Us
            </a>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 1000px) { .d-nav { display: none !important; } .hamburger { display: flex !important; } }
        @keyframes mSlideDown { from { opacity:0; transform:translateY(-10px); } to { opacity:1; transform:translateY(0); } }
      `}</style>
    </nav>
  );
}
